/** The catalogue page's notices, all from the rows endpoint's `meta` (computed over the whole
 *  account, independent of the filters): the import gate, the tenant's AI gates and malformed
 *  settings, `package_ids` drift and the legs coverage warnings (with "include its legs"). */
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as catalogueApi from '@/api/catalogue';
import type { CatalogueCoverageWarning, CatalogueMeta } from '@/api/catalogue';
import { useSnackbar } from '@/components/Snackbar';

import { KEY } from './catalogueModel';
import { useInvalidateCatalogue } from './queries';

const LEG_REASONS: Record<string, string> = {
  not_selected: 'not selected',
  no_tour: 'selected, but builds no tour',
  not_in_catalogue: 'not in this catalogue',
};

/** The import gate, when it is closed. `catalogue_sync_disabled` is the case that matters: a
 *  gated restech whose tenant has not opted in (live tenants are never flipped). */
export function CatalogueGateAlert({
  meta,
  integration,
  configUrl,
}: {
  meta: CatalogueMeta;
  integration: string;
  configUrl: string | null;
}) {
  const blocker = meta.gate.import_blocker;
  if (meta.gate.can_import || !blocker) return null;
  const disabled = blocker.code === 'catalogue_sync_disabled';
  const keys = blocker.config_keys.length ? blocker.config_keys : disabled ? ['catalogue_sync'] : [];
  return (
    <Alert
      severity={disabled ? 'warning' : 'info'}
      sx={{ mb: 2 }}
      action={
        configUrl ? (
          <Button size="small" color="inherit" component={RouterLink} to={configUrl} target="_blank">
            Configure
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>
        {disabled
          ? 'Catalogue sync is not enabled for this tenant'
          : 'This integration cannot import its catalogue here'}
      </AlertTitle>
      {blocker.detail}
      {keys.length > 0 && (
        <>
          {' '}
          Config key:{' '}
          {keys.map((k, i) => (
            <Fragment key={k}>
              {i > 0 && ', '}
              <code>{k}</code>
            </Fragment>
          ))}{' '}
          on this operator&apos;s {integration} config.
        </>
      )}
      {disabled &&
        ' The setup wizard turns it on for new tenants; a live tenant keeps mapping its products by hand (Fetch products, then Map selected as…).'}
    </Alert>
  );
}

export function TenantGateNotes({ meta }: { meta: CatalogueMeta }) {
  const ts = meta.tenant_settings;
  const off: string[] = [];
  if (!ts.ai_sync.tours) off.push('tours');
  if (!ts.ai_sync.itineraries) off.push('itinerary cards');
  return (
    <>
      {off.length > 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Catalogue syncs create no {off.join(' and ')} for this tenant (Settings tab).
          {!ts.ai_sync.tours &&
            ' Without synced tours, bookings only import once ops create or map tours by hand.'}{' '}
          Existing synced objects are left as they are.
        </Alert>
      )}
      {ts.problems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Malformed catalogue settings fall back to their defaults: <code>{ts.problems.join(', ')}</code>.
        </Alert>
      )}
    </>
  );
}

export function PackageDriftAlert({ meta }: { meta: CatalogueMeta }) {
  const drift = meta.package_ids_drift;
  const products = drift.products;
  const extra = drift.extra_ids;
  if (!products.length && !extra.length) return null;
  return (
    <Alert severity="warning" sx={{ mb: 1.5 }}>
      <AlertTitle>package_ids disagrees with the Package import setting</AlertTitle>
      Booking import reads the integration&apos;s <code>package_ids</code>; this page writes it through
      from each package&apos;s setting. It was edited elsewhere, or is older than the catalogue — it is
      reported, never fixed silently.
      {products.length > 0 && (
        <Box component="ul" sx={{ my: 0.5, pl: 2 }}>
          {products.slice(0, 8).map((p) => (
            <li key={p.id}>
              {p.name} (<code>{p.vendor_id}</code>): setting says <strong>{p.mode}</strong>, package_ids{' '}
              {p.listed ? 'lists it' : 'does not list it'}
            </li>
          ))}
          {products.length > 8 && <li>…and {products.length - 8} more</li>}
        </Box>
      )}
      {extra.length > 0 && (
        <Typography variant="body2">
          {extra.length} listed id{extra.length === 1 ? ' is' : 's are'} no package of this catalogue
          (hand-added or removed upstream): <code>{extra.slice(0, 5).join(', ')}</code>
          {extra.length > 5 ? '…' : ''}
        </Typography>
      )}
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Setting a package&apos;s Package import again rewrites its membership.
      </Typography>
    </Alert>
  );
}

/** Selected packages imported as legs whose legs would import nothing (plan §3.5). */
export function CoverageAlert({
  tenantId,
  warnings,
}: {
  tenantId: number;
  warnings: readonly CatalogueCoverageWarning[];
}) {
  const { show } = useSnackbar();
  const invalidate = useInvalidateCatalogue(tenantId);
  const includeLegs = useMutation({
    mutationFn: (legIds: number[]) =>
      catalogueApi.bulkSettings(tenantId, {
        product_ids: legIds,
        key: KEY.include,
        value: true,
        note: 'include the legs of a package imported as legs',
      }),
    onSuccess: async (r) => {
      show(`${r.changed_ids.length} leg(s) included.`, 'success');
      await invalidate();
    },
    onError: (e) => {
      const refusals = catalogueApi.settingErrorsOf(e);
      show(refusals?.map((x) => x.detail).join(' ') || (e as Error).message, 'error');
    },
  });
  if (!warnings.length) return null;
  return (
    <Alert severity="warning" sx={{ mb: 1.5 }}>
      <AlertTitle>
        {warnings.length} package{warnings.length === 1 ? '' : 's'} imported as legs would import nothing for
        some legs
      </AlertTitle>
      A package in Legs mode gets no tour: its bookings are split onto the legs, so every non-lodging leg
      needs a tour of its own.
      <Box component="ul" sx={{ my: 0.5, pl: 2 }}>
        {warnings.slice(0, 8).map((w) => {
          const selectable = w.legs.filter((l) => l.reason === 'not_selected' && l.leg_id != null);
          return (
            <li key={w.id}>
              <strong>{w.name}</strong>{' '}
              {w.status === 'unknown'
                ? '— legs unknown until the package detail is fetched (refresh the listing).'
                : w.reason === 'no_legs'
                  ? '— no legs in its detail.'
                  : `— ${w.legs
                      .map((l) => `${l.product_id} (${LEG_REASONS[l.reason] ?? l.reason})`)
                      .join(', ')}`}
              {selectable.length > 0 && (
                <Button
                  size="small"
                  sx={{ ml: 1, py: 0 }}
                  disabled={includeLegs.isPending}
                  onClick={() => includeLegs.mutate(selectable.map((l) => l.leg_id as number))}
                >
                  Include its legs
                </Button>
              )}
            </li>
          );
        })}
        {warnings.length > 8 && <li>…and {warnings.length - 8} more (filter Packages only)</li>}
      </Box>
    </Alert>
  );
}
